import { useLocation, NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import clsx from 'clsx'
import {
  Box,
  Columns,
  Column,
  Text,
  Stack,
  IconBolt,
  TextLink,
  PopoverRenderer
} from '@machobear/design-system'
import { vars } from '@machobear/design-system/css'
import { Userpilot } from 'userpilot'
import config from '@/config'
import {
  MoneyWithdrawIcon,
  BuildingIcon,
  UserIcon,
  PhoneIcon,
  LockSmallIcon,
  TrashSmallIcon,
  QuestionCircleIcon,
  BellIcon,
  AppsIcon,
  WalletIcon,
  AutoReplyIcon
} from '@/components/icons'
import Skeleton from '@/components/skeleton'
import useRole from '@/hooks/use-role'
import useProfile from '@/hooks/use-profile'
import useMediaQuery from '@/hooks/use-media-query'
import { breakpoints } from '@/styles'
import * as styles from './index.css'

const { WEB_URL } = config
const isDev = config.BUILD_ENV === 'dev'
const activeClassName = 'nav-item-active'
const MenuItem = styled(NavLink)`
  color: ${vars.foregroundColor.navItem};
  :hover {
    color: ${vars.foregroundColor.navItemActive};
  }

  &.${activeClassName} {
    color: ${vars.foregroundColor.navItemActive};
  }

  &.disabled {
    color: ${vars.foregroundColor.disabled};
  }
`

const LoadingMenu = () => (
  <Box padding="xxxxsmall">
    <Skeleton height={16} width={200} />
  </Box>
)

const UpgradeAccountPopover = ({ trackingId }) => {
  const handleClick = e => {
    trackingId && Userpilot.track(trackingId)
  }
  const { t } = useTranslation()
  return (
    <PopoverRenderer
      trigger={({ triggerProps }) => (
        <Box
          {...triggerProps}
          aria-label="trigger popover"
          component="span"
          background="promoteLight"
          borderRadius="small"
          padding="xxxsmall"
          marginY="xxsmall"
        >
          <IconBolt tone="promote" />
        </Box>
      )}
    >
      <Stack space="medium">
        <Text size="small">
          {t(
            'l.settings.upgrade_account.text',
            "Looking to save time? Upgrade to liv.rent's Growth or Business plans to unlock this and other premium time-saving features."
          )}
        </Text>
        <Box marginBottom="xxxsmall">
          <Text size="small" weight="strong">
            <TextLink href={`${WEB_URL}/pricing`} target="_blank" onClick={handleClick}>
              {t('l.settings.upgrade_account.link', 'Upgrade Account')}
            </TextLink>
          </Text>
        </Box>
      </Stack>
    </PopoverRenderer>
  )
}

const SideMenu = () => {
  const displayDesktopSmall = useMediaQuery(`(max-width: ${breakpoints.desktopSmall})`)
  const { t } = useTranslation()
  const role = useRole()
  const data = useProfile()
  const isEssentialPlan = data.company_info?.base_plan_name === 'essentials-plan-monthly'
  const isTenant = role === 'tenant'

  const LANDLORD_SETTINGS = [
    {
      title: t('l.settings.view.plan_settings.label', 'Plan & Billing'),
      description: t(
        'l.settings.view.plan_settings.text',
        'See your current plan and manage your billing information.'
      ),
      icon: WalletIcon,
      to: '/settings/plan-settings',
      key: ['plan-settings']
    },
    {
      title: t('b.settings.view.payment_settings.label', 'Payment Settings'),
      description: t(
        'b.settings.view.payment_settings.text',
        'Set up financial accounts to receive rental payments.'
      ),
      icon: MoneyWithdrawIcon,
      to: '/settings/payment-settings',
      key: ['payment-settings']
    }
  ]

  const TENANT_SETTINGS = [
    {
      title: t('b.settings.view.payment_settings.label', 'Payment Settings'),
      description: t(
        'b.settings.view.payment_settings_tenant.text',
        'Set up financial accounts to make rental payments'
      ),
      icon: MoneyWithdrawIcon,
      to: '/settings/payment-settings',
      key: ['payment-settings']
    }
  ]

  const MANAGE_SETTINGS_ESSENTIALS = [
    {
      title: t('l.settings.view.manage_user.label', 'Manage Users'),
      description: t(
        'l.settings.view.manage_user.text',
        'Add users from your account and assign listings to them.'
      ),
      icon: UserIcon,
      to: '',
      key: ['users'],
      isDisabled: true,
      popover: UpgradeAccountPopover,
      popoverTrackingId: 'settings_manageusers_click_upgradeaccount_anchortxt'
    },
    {
      title: t('l.settings.view.manage_listing.label', 'Manage Listings'),
      description: t(
        'b.settings.view.manage_listing.text',
        'Reassign listings to users within your organization.'
      ),
      icon: BuildingIcon,
      to: '',
      key: ['listings'],
      isDisabled: true,
      popover: UpgradeAccountPopover,
      popoverTrackingId: 'settings_managelistings_click_upgradeaccount_anchortxt'
    },
    {
      title: t('l.settings.view.auto_reply.label', 'Auto-Reply'),
      description: t(
        'b.settings.view.auto_reply.text',
        'Set up automated responses to save time responding to messages and send them automatically.'
      ),
      icon: AutoReplyIcon,
      to: '/settings/auto-reply',
      key: ['auto-reply'],
      isDisabled: false
    }
  ]

  const MANAGE_SETTINGS = [
    {
      title: t('l.settings.view.manage_user.label', 'Manage Users'),
      description: t(
        'l.settings.view.manage_user.text',
        'Add users from your account and assign listings to them.'
      ),
      icon: UserIcon,
      to: '/manage/users/',
      key: ['users']
    },
    {
      title: t('l.settings.view.manage_listing.label', 'Manage Listings'),
      description: t(
        'b.settings.view.manage_listing.text',
        'Reassign listings to users within your organization.'
      ),
      icon: BuildingIcon,
      to: '/manage/listings/',
      key: ['listings']
    },
    {
      title: t('l.settings.view.auto_reply.label', 'Auto-Reply'),
      description: t(
        'b.settings.view.auto_reply.text',
        'Set up automated responses to save time responding to messages and send them automatically.'
      ),
      icon: AutoReplyIcon,
      to: '/settings/auto-reply',
      key: ['auto-reply']
    }
  ]

  const NOTIFICATIONS_AND_PREFERENCES = [
    {
      title: t('b.settings.view.notifications.link', 'Notifications'),
      icon: BellIcon,
      to: `/settings/notifications`,
      key: ['notifications']
    },
    {
      title: t('b.settings.view.preferences.link', 'Preferences'),
      icon: AppsIcon,
      to: `/settings/preferences`,
      key: ['preferences']
    }
  ]

  const ACCOUNT_SETTINGS = [
    {
      title: t('b.settings.view.change_phone_number.label', 'Phone Number'),
      description: t(
        'b.settings.view.change_phone_number.text',
        'Change your login phone number here.'
      ),
      icon: PhoneIcon,
      to: '/settings/change-phone-number',
      key: ['change-phone-number']
    },
    {
      title: t('b.settings.view.change_password.label', 'Login & Password'),
      description: t(
        'b.settings.view.change_password.text',
        'Connect social accounts & change password.'
      ),
      icon: LockSmallIcon,
      to: '/settings/social-login-password',
      key: ['social-login-password']
    },
    {
      title: t('b.settings.view.delete_my_account.label', 'Delete My Account'),
      description: t('b.settings.view.delete_my_account.text', 'No longer need us?'),
      icon: TrashSmallIcon,
      to: '/settings/feedback',
      key: ['feedback', 'delete-account']
    },
    {
      title: t('b.settings.view.support.label', 'Support'),
      description: t('b.settings.view.delete_my_account.text', 'No longer need us?'),
      icon: QuestionCircleIcon,
      to: '/settings/support',
      key: ['support']
    }
  ]

  const DEV_SETTINGS = [
    {
      title: 'Dev Tools',
      icon: UserIcon,
      to: '/settings/dev-tools',
      key: ['dev']
    }
  ]

  return (
    <Box
      component="menu"
      background="neutralLight"
      style={{
        borderRight: `1px solid ${vars.borderColor.neutralLight}`
      }}
      height="full"
      padding={displayDesktopSmall ? 'large' : 'xxlarge'}
    >
      <Stack space="large" align="left">
        <OptionList
          title={t('b.settings.view.payments.heading', 'Payments')}
          data={isTenant ? TENANT_SETTINGS : LANDLORD_SETTINGS}
        />
        {!isTenant && isEssentialPlan && (
          <OptionList
            title={t('b.settings.view.listing_management.heading', 'Listing Management')}
            data={MANAGE_SETTINGS_ESSENTIALS}
          />
        )}
        {!isTenant && !isEssentialPlan && (
          <OptionList
            title={t('b.settings.view.listing_management.heading', 'Listing Management')}
            data={MANAGE_SETTINGS}
          />
        )}
        <OptionList
          title={t('b.settings.view.notifications.heading', 'Notifications & Preferences')}
          data={NOTIFICATIONS_AND_PREFERENCES}
        />
        <OptionList
          title={t('b.settings.view.account.heading', 'Account & Security')}
          data={ACCOUNT_SETTINGS}
        />
        {!!isDev && <OptionList title="Dev Tools" data={DEV_SETTINGS} />}
      </Stack>
    </Box>
  )
}

const OptionList = ({ title, data }) => {
  const location = useLocation()
  const pathName = location.pathname
  const profile = useProfile()
  const isLoading = !profile.company_info

  const handleClick = (e, isDisabled) => {
    if (isDisabled) {
      e.preventDefault()
    }
  }

  return (
    <Stack space="small" align="left">
      {isLoading ? (
        <Skeleton height={16} width={200} />
      ) : (
        <Text tone="secondary" size="small">
          {title}
        </Text>
      )}
      <Stack space="xxsmall">
        {data.map(item => {
          const Icon = item.icon
          const isActive = item.key.some(itemKey => pathName.includes(itemKey))
          return isLoading ? (
            <LoadingMenu key={item.key} />
          ) : (
            <Box
              component="li"
              borderRadius="small"
              key={item.key}
              className={`${styles.menuItem} ${clsx({
                [styles.menuItemActive]: isActive
              })}`}
            >
              <Box display="flex" justifyContent="spaceBetween">
                <MenuItem
                  activeClassName={item.isDisabled ? undefined : activeClassName}
                  className={item.isDisabled ? 'disabled' : undefined}
                  onClick={e => handleClick(e, item.isDisabled)}
                  to={item.to}
                >
                  <Box padding="xxsmall" borderRadius="small">
                    <Columns space="small" alignY="center">
                      <Column width="content">
                        <Box display="flex" className={styles.iconWrapper}>
                          <Icon />
                        </Box>
                      </Column>
                      <Column>
                        <Text tone="primary" size="small" weight="medium">
                          {item.title}
                        </Text>
                      </Column>
                    </Columns>
                  </Box>
                </MenuItem>
                {item.popover && <item.popover trackingId={item.popoverTrackingId} />}
              </Box>
            </Box>
          )
        })}
      </Stack>
    </Stack>
  )
}

export default SideMenu
